import React, { useState, useCallback, useRef, useMemo, ComponentType } from 'react';

import {
    cn,
    Portal, ClosingOverlay,
    useSizeContext,
    TransitionComponent,
    animation,
} from 'Modules/ui';

import styles from './index.module.scss';
import { PopupWithButtonProps, PopupLocationClassNames, PopupProps } from './types';


const getPopupProps = (
    elem: HTMLDivElement,
    classNames: PopupLocationClassNames = {},
): PopupProps => {
    if (!elem)
        return {};

    const { left, top, right, bottom } = elem.getBoundingClientRect();
    const { clientWidth, clientHeight } = document.documentElement;
    const xProps = left + right > clientWidth
        ? { right: clientWidth - right }
        : { left };
    const yProps = top + bottom > clientHeight
        ? { bottom: clientHeight - bottom }
        : { top };
    return {
        style: { ...xProps, ...yProps },
        className: cn(
            styles.popup,
            top + bottom > clientHeight
                ? cn(styles.bottom, classNames.bottom)
                : cn(styles.top, classNames.top),
            left + right > clientWidth
                ? cn(styles.right, classNames.right)
                : cn(styles.left, classNames.left),
        ),
    };
};

export const PopupWithButton: ComponentType<PopupWithButtonProps> = ({
    classNames = {}, button: Button, children,
    suppressPopup, onOpen,
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const popupRef = useRef<HTMLDivElement>();
    const { className: popupClassName, ...popupProps } = useMemo(
        () => getPopupProps(popupRef.current, classNames.popupLocation),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isOpen, classNames, useSizeContext()],
    );

    const showPopup = useCallback(() => {
        if (onOpen)
            onOpen();
        setIsOpen(true);
    }, [onOpen]);
    const hidePopup = useCallback(() => setIsOpen(false), []);

    const renderedButton = (
        <Button className={styles.button} show={showPopup} hide={hidePopup} isOpen={isOpen} />
    );

    return (
        <div ref={popupRef} className={classNames.container}>
            {renderedButton}
            <Portal>
                <TransitionComponent in={isOpen && !suppressPopup} timeout={animation}>
                    <div className={cn(popupClassName, classNames.popup)} {...popupProps}>
                        {renderedButton}
                        <div className={cn(styles.content, classNames.content)}>
                            {children}
                        </div>
                    </div>
                </TransitionComponent>
            </Portal>
            {isOpen && <ClosingOverlay zIndex={49} onClose={hidePopup} />}
        </div>
    );
};

export * from './types';
